// Utility for persisting a "join-intent" (or any deep-link) target across the
// authentication flow. Because e-mail verification can happen in a different
// tab or on another device, we lean on localStorage rather than router state so
// the intended destination survives full page reloads and navigations.

const STORAGE_KEY = "solofix_post_auth_redirect";

// Maximum age of a stored intent before we consider it stale (24 hours).
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface StoredRedirect {
  path: string;
  ts: number;
}

/**
 * Validate that a path is a safe, relative in-app destination.
 * Rejects absolute URLs, protocol-relative URLs (`//host`) and anything that
 * does not start with a single `/` — this prevents open-redirect abuse.
 */
const isSafeRelativePath = (path: unknown): path is string => {
  if (typeof path !== "string" || path.length === 0) {
    return false;
  }
  // Must start with a single slash, but not a double slash (protocol-relative).
  if (!path.startsWith("/") || path.startsWith("//")) {
    return false;
  }
  // Reject anything that looks like it carries a scheme (e.g. `/\evil`,
  // backslashes can be normalised to slashes by some browsers).
  if (path.includes("\\") || path.includes("://")) {
    return false;
  }
  return true;
};

/**
 * Persist the given path as the destination to return to after authentication.
 * Silently ignores unsafe paths.
 */
export const savePostAuthRedirect = (path: string): void => {
  if (!isSafeRelativePath(path)) {
    return;
  }
  try {
    const payload: StoredRedirect = { path, ts: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage may be unavailable (private mode / quota) — fail silently.
  }
};

/**
 * Read the stored redirect target WITHOUT removing it. Returns null when
 * missing, malformed, unsafe or expired (and clears stale/invalid entries).
 */
export const peekPostAuthRedirect = (): string | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as StoredRedirect;
    if (!parsed || !isSafeRelativePath(parsed.path)) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    if (typeof parsed.ts !== "number" || Date.now() - parsed.ts > MAX_AGE_MS) {
      // Stale intent — drop it so we don't send the user somewhere unexpected.
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return parsed.path;
  } catch {
    // Corrupted value — clear it defensively.
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    return null;
  }
};

/**
 * Read the stored redirect target and clear it. Returns null when there is no
 * valid, fresh target. Use this at the final step of an auth flow.
 */
export const consumePostAuthRedirect = (): string | null => {
  const path = peekPostAuthRedirect();
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  return path;
};

/**
 * Explicitly clear any stored redirect target.
 */
export const clearPostAuthRedirect = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};
